import { useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { isTDOT } from '../constants/stores';

// Multiple selectors to watch for 'active' class
const targetSelectors = [
  'div.vehicle-navigator-form-wrapper > div > div.form-container > div > div.fields-container > div > div:nth-child(1) > div',
  'div.vehicle-navigator-form-wrapper > div > div.form-container > div > div.fields-container > div > div:nth-child(2) > div',
  'div.vehicle-navigator-form-wrapper > div > div.form-container > div > div.fields-container > div > div:nth-child(3) > div',
] as const;

/**
 * React hook that overrides z-index on .CampaignType--slide when TDOT dropdown is active on mobile devices.
 *
 * The hook uses a MutationObserver to watch for DOM changes and automatically overrides
 * the z-index on .CampaignType--slide to 1 when any of the specific dropdown elements
 * have the 'active' class. Only works on TDOT stores, mobile devices, and when available to current user.
 *
 * Usage:
 *   useOverrideZIndexForTdotDropdown({ storeId, isAvailableToCurrentUser });
 *
 * @param storeId - The store ID to check if it's TDOT store.
 * @param isAvailableToCurrentUser - Whether the current user should see the override functionality.
 */
export function useOverrideZIndexForTdotDropdown({
  storeId,
  isAvailableToCurrentUser,
}: {
  storeId?: string;
  isAvailableToCurrentUser: boolean;
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Store current values in refs so MutationObserver always has access to latest values
  const currentValuesRef = useRef({
    storeId,
    isAvailableToCurrentUser,
    isMobile,
  });

  // Update refs on every render to ensure observer has current values
  currentValuesRef.current = {
    storeId,
    isAvailableToCurrentUser,
    isMobile,
  };

  const observersRef = useRef<{
    domObserver: MutationObserver | null;
    isOverriding: boolean;
    throttleTimeoutId: NodeJS.Timeout | null;
  }>({
    domObserver: null,
    isOverriding: false,
    throttleTimeoutId: null,
  });

  const originalZIndexRef = useRef<{
    el: HTMLElement | null;
    value: string | null;
  }>({ el: null, value: null });

  useEffect(() => {
    const cleanup = () => {
      // Clear throttle timeout
      if (observersRef.current.throttleTimeoutId) {
        clearTimeout(observersRef.current.throttleTimeoutId);
        observersRef.current.throttleTimeoutId = null;
      }

      // Disconnect observer
      if (observersRef.current.domObserver) {
        observersRef.current.domObserver.disconnect();
        observersRef.current.domObserver = null;
      }
      observersRef.current.isOverriding = false;
    };

    const restoreOriginalZIndex = (element: HTMLElement) => {
      try {
        if (
          originalZIndexRef.current.el === element &&
          originalZIndexRef.current.value !== null
        ) {
          element.style.zIndex = originalZIndexRef.current.value;
          // Clear the reference since we've restored it
          originalZIndexRef.current = { el: null, value: null };
        }
      } catch (error) {
        console.warn('Error restoring original z-index:', error);
      }
    };

    const overrideZIndex = (element: HTMLElement) => {
      try {
        // Save the original z-index only if this is a new element
        if (originalZIndexRef.current.el !== element) {
          originalZIndexRef.current = {
            el: element,
            value: element.style.zIndex || '',
          };
        }

        element.style.setProperty('z-index', '1', 'important');
        observersRef.current.isOverriding = true;
      } catch (error) {
        console.warn('Error overriding z-index:', error);
      }
    };

    const checkAndOverrideZIndex = () => {
      try {
        // Always get current values from ref (never stale!)
        const { storeId, isAvailableToCurrentUser, isMobile } =
          currentValuesRef.current;

        // Check all 3 required conditions
        const isTdotStore = isTDOT(storeId);

        if (!isTdotStore || !isMobile || !isAvailableToCurrentUser) {
          // If conditions aren't met, restore original z-index if we were overriding
          if (observersRef.current.isOverriding) {
            const campaignElement = document.querySelector(
              '.CampaignType--slide'
            ) as HTMLElement | null;
            if (campaignElement) {
              restoreOriginalZIndex(campaignElement);
            }
            observersRef.current.isOverriding = false;
          }
          return;
        }

        const campaignElement = document.querySelector(
          '.CampaignType--slide'
        ) as HTMLElement | null;

        if (!campaignElement || !document.contains(campaignElement)) {
          if (observersRef.current.isOverriding) {
            observersRef.current.isOverriding = false;
          }
          return;
        }

        // Check all target selectors for 'active' class
        let hasActiveElement = false;

        for (const selector of targetSelectors) {
          const dropdownElement = document.querySelector(
            selector
          ) as HTMLElement | null;
          if (
            dropdownElement &&
            document.contains(dropdownElement) &&
            dropdownElement.classList.contains('active')
          ) {
            hasActiveElement = true;
            break; // Found one active element, that's enough
          }
        }

        if (hasActiveElement) {
          if (!observersRef.current.isOverriding) {
            overrideZIndex(campaignElement);
          }
        } else {
          // No active dropdown found, restore if we were overriding
          if (observersRef.current.isOverriding) {
            restoreOriginalZIndex(campaignElement);
            observersRef.current.isOverriding = false;
          }
        }
      } catch (error) {
        console.warn('Error in checkAndOverrideZIndex:', error);
      }
    };

    // Throttled version of the z-index check to prevent excessive calls
    const throttledCheck = () => {
      if (observersRef.current.throttleTimeoutId) {
        clearTimeout(observersRef.current.throttleTimeoutId);
      }

      observersRef.current.throttleTimeoutId = setTimeout(() => {
        checkAndOverrideZIndex();
      }, 16); // ~60fps throttling
    };

    // Initial check
    checkAndOverrideZIndex();

    // Set up DOM observer to watch for changes
    try {
      observersRef.current.domObserver = new MutationObserver(throttledCheck);

      // Watch for changes in the document
      observersRef.current.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'], // Focus on class changes for 'active' detection
      });
    } catch (error) {
      console.warn('Error setting up DOM observer:', error);
    }

    return () => {
      cleanup();
      // Restore original z-index on cleanup
      try {
        const campaignElement = document.querySelector(
          '.CampaignType--slide'
        ) as HTMLElement | null;
        if (campaignElement) {
          restoreOriginalZIndex(campaignElement);
        }
      } catch (error) {
        console.warn('Error during cleanup restoration:', error);
      }
    };
  }, []);
}
